import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import DevotionalWeeklyCard from './devotionalWeeklyCard';


function ArticleSkeleton() {
  return (
    <div className="dna-article-card dna-skeleton-card" aria-hidden="true">
      <div className="dna-card-thumb dna-skeleton-box" />
      <div className="dna-card-body">
        <div className="dna-skeleton-line dna-skeleton-line--title" />
        <div className="dna-skeleton-line dna-skeleton-line--desc" />
        <div className="dna-skeleton-line dna-skeleton-line--desc dna-skeleton-line--short" />
      </div>
    </div>
  );
}


const Articles = ({ cat_id, ssrData = [], limit ,ssrCatId}) => {
const hydratedItems = Array.isArray(ssrData)
  ? ssrData.map(item => ({
      img: item.image_url,
      title: item.cont_name,
      url: item.category_url,
    }))
  : [];

  const [data, setData] = useState(hydratedItems);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const startRef = useRef(limit);
  const isFetchingRef = useRef(false);
  const prevCatIdRef = useRef(cat_id);
  const hydratedRef = useRef(false);
  const { ref: loaderRef, inView } = useInView({ threshold: 0.5 });

  // Hydrate from SSR or fetch on category change
  useEffect(() => {
    const shouldHydrate =
      Array.isArray(ssrData) &&
      ssrData.length > 0 &&
      prevCatIdRef.current === cat_id === ssrCatId;

    if (shouldHydrate) {
      setData(hydratedItems);
      startRef.current = hydratedItems.length;
      setHasMore(hydratedItems.length === limit);
      hydratedRef.current = true;
      prevCatIdRef.current = cat_id
      setLoading(false);
    } else if (!hydratedRef.current) {
      getData(); // Only fetch if not hydrated
    }

    prevCatIdRef.current = cat_id;
  }, [cat_id]);

  const getData = useCallback(async () => {
    if (isFetchingRef.current || !hasMore) return;

    isFetchingRef.current = true;
    setLoading(true);

    try {
      const response = await fetch(
        `https://www.teluguone.com/devotional_api/devotionalCatSubPageJson?cat_id=${cat_id}&start=${startRef.current}&limit=${limit}`
      );
      if (!response.ok) throw new Error("Server Response Was Not Ok");

      const json = await response.json();
      const validItems = json.devotional_categories?.filter(item => !item.msg) || [];

      const mapped = validItems.map(item => ({
        img: item.image_url,
        title: item.cont_name,
        url: item.category_url,
      }));

      setData(prev => [...prev, ...mapped]);
      startRef.current += mapped.length;
      setHasMore(mapped.length === limit);
      setError(null);
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.message || 'Unknown Error');
      setHasMore(false);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
      prevCatIdRef.current = cat_id
    }
  }, [cat_id, limit, hasMore]);

  // Load more on scroll
  useEffect(() => {
    if (inView && !loading && hasMore && !isFetchingRef.current) {
      getData();
    }
  }, [inView, loading, hasMore, getData]);

  // UI Fallback while loading initially
  if (loading && data.length === 0) {
    return (
      <div className='row'>
        {[...Array(4)].map((_, i) => (
          <div className='col-12 col-sm-6' key={i}>
             <ArticleSkeleton />
          </div>
        ))}
      </div>
    );
  }

  // Error UI
  if (error) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="json-loading-error-div">
            <img src="/devotional/assets/images/json_error.png" alt="json_error" />
            <p className='text-danger text-center'>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='row g-0' style={{border:"1px solid #e8ddd0",backgroundColor:"#e8ddd0"}} >
      <div className="col-12 devotional-videos-header">Articles</div>

      {Array.isArray(data) && data.map((item, idx) => (
        <DevotionalWeeklyCard
          key={item.url || idx}
          item={item}
          index={idx}
        />
      ))}

      {loading && data.length > 0 && (
        <div className="col-12 col-sm-6 d-flex">
            <ArticleSkeleton />
            <ArticleSkeleton />
        </div>
      )}

      {!loading && hasMore && (
        <div ref={loaderRef} className="col-12 text-center">
          <span>Loading more...</span>
        </div>
      )}

      {!hasMore && data.length > 0 && (
        <div className="text-center mt-3 mb-3">No more posts</div>
      )}

      {!hasMore && data.length === 0 && !loading && (
        <div className="text-center mt-3 mb-3">No posts found</div>
      )}
    </div>
  );
};

export default Articles;
